import { Link, useRouterState } from '@tanstack/react-router';
import { ChevronDown, CircleHelp, LogOut, Search, UserRound } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Me } from '../api.ts';
import { logout } from '../auth.tsx';
import { activeItemTo, itemsOf, locate, type NavArea } from '../navigation.ts';
import { useShell } from './slots.tsx';
import { usePlatformLook } from '../platform.tsx';
import { FooterIcon, settingsEntry } from './menu.tsx';

/**
 * Menu no topo (escolha do tema, ADR 0117): no computador, uma faixa com o logo, os departamentos e,
 * à direita, a busca, a Configuração, a pessoa, a Ajuda e Sair. Quando o departamento aberto tem
 * mais de uma tela, elas aparecem numa segunda linha. No celular continua o menu que abre de lado.
 */
export function TopBar({
  areas,
  me,
  onSearch,
}: {
  areas: NavArea[];
  me: Me;
  onSearch: () => void;
}) {
  const { t } = useTranslation();
  const look = usePlatformLook();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const current = locate(areas, pathname).area;
  const subnav = useShell()?.options.subnav ?? 'strip';
  const activeTo = activeItemTo(areas, pathname);
  const { settingsArea, settings } = settingsEntry(areas);
  const inSettings = pathname === '/settings' || pathname.startsWith('/settings/');
  // A Configuração tem o próprio submenu; o Início não tem telas para listar.
  const screens =
    current && !current.bottom && current.id !== 'home' && !inSettings ? itemsOf(current) : [];
  const link = (active: boolean) =>
    `flex min-h-9 shrink-0 items-center gap-1.5 rounded-md px-3 text-sm whitespace-nowrap ${
      active ? 'ui-nav-active font-medium' : 'text-ink hover:bg-paper'
    }`;
  return (
    <div className="ui-sidebar hidden shrink-0 border-b border-rule md:block">
      <div className="flex items-center gap-4 px-6 py-2">
        <Link to="/" aria-label={t('nav.homeBrand')} className="flex shrink-0 items-center">
          <img src={look.logoUrl} alt="" className="h-8 w-auto max-w-44 object-contain" />
        </Link>
        <nav
          aria-label={t('nav.label')}
          className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto"
        >
          {areas
            .filter((a) => !a.bottom)
            .map((area) => {
              const Icon = area.icon;
              const active = area.id === current?.id && !inSettings;
              return (
                <Link
                  key={area.id}
                  to={itemsOf(area)[0]!.to}
                  aria-current={active ? 'true' : undefined}
                  className={link(active)}
                >
                  <Icon size={16} strokeWidth={1.75} aria-hidden className="shrink-0" />
                  {t(area.key)}
                </Link>
              );
            })}
        </nav>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={onSearch}
            aria-label={t('nav.search')}
            title={`${t('nav.search')} (Ctrl K)`}
            className="flex size-9 items-center justify-center rounded-md text-muted hover:bg-paper hover:text-ink"
          >
            <Search size={18} strokeWidth={1.75} aria-hidden />
          </button>
          {settings && (
            <FooterIcon
              to={settings.to}
              label={t(settingsArea!.key)}
              icon={settingsArea!.icon}
              active={inSettings}
            />
          )}
          <FooterIcon
            to="/help"
            label={t('nav.help')}
            icon={CircleHelp}
            active={pathname === '/help'}
          />
          <Link
            to="/account"
            title={`${t('nav.account')} (${me.user.email})`}
            aria-current={pathname === '/account' ? 'page' : undefined}
            className={`${link(pathname === '/account')} max-w-48 font-medium`}
          >
            <UserRound size={16} strokeWidth={1.75} aria-hidden className="shrink-0" />
            <span className="truncate">{me.user.displayName}</span>
          </Link>
          <button
            type="button"
            onClick={() => void logout()}
            className="flex min-h-9 shrink-0 items-center gap-1.5 rounded-md px-2 text-sm text-brand-text hover:underline"
          >
            <LogOut size={16} strokeWidth={1.75} aria-hidden />
            {t('auth.logout')}
          </button>
        </div>
      </div>
      {screens.length > 1 &&
        (subnav === 'dropdown' ? (
          // Telas da área numa lista que abre (ajuste `subnav`, ADR 0133): ocupa uma linha só.
          <details className="group relative px-6 pb-2">
            <summary className="flex w-fit cursor-pointer list-none items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium hover:bg-paper [&::-webkit-details-marker]:hidden">
              {t(screens.find((i) => i.to === activeTo)?.key ?? current!.key)}
              <ChevronDown size={14} aria-hidden />
            </summary>
            <nav
              aria-label={t(current!.key)}
              className="ui-panel absolute z-30 mt-1 flex min-w-56 flex-col rounded-lg bg-sheet p-1"
            >
              {screens.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  aria-current={item.to === activeTo ? 'page' : undefined}
                  className={link(item.to === activeTo)}
                >
                  {t(item.key)}
                </Link>
              ))}
            </nav>
          </details>
        ) : (
          <nav
            aria-label={t(current!.key)}
            className="flex items-center gap-1 overflow-x-auto px-6 pb-2"
          >
            {screens.map((item) => {
              const Icon = item.icon;
              const active = item.to === activeTo;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  aria-current={active ? 'page' : undefined}
                  className={link(active)}
                >
                  <Icon size={16} strokeWidth={1.75} aria-hidden className="shrink-0" />
                  {t(item.key)}
                </Link>
              );
            })}
          </nav>
        ))}
    </div>
  );
}
